// Knowledge Article API Service
import { apiRequest } from '../services/apiClient';
import { getCurrentUser } from './authService';

function getTenantIdFromSession() {
  const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('tenantId') : null;
  if (raw == null || raw === '') return undefined;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

/** Prefer explicit option → JWT tenant → session (portal + agent). */
function resolveTenantIdForReadParams(options = {}) {
  if (options.tenantId !== undefined && options.tenantId !== null) {
    const n = Number(options.tenantId);
    return Number.isFinite(n) ? n : undefined;
  }
  const u = getCurrentUser();
  if (u?.tenantId != null && u.tenantId !== '') {
    const n = Number(u.tenantId);
    if (Number.isFinite(n)) return n;
  }
  return getTenantIdFromSession();
}

/**
 * Extract list payload from registration ResponseBean after articleService wraps `{ data: apiJson }`.
 * Handles occasional extra nesting from gateways.
 */
export function extractRegistrationListData(serviceResult) {
  const body = serviceResult?.data;
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (body.data != null && typeof body.data === 'object' && Array.isArray(body.data.data)) {
    return body.data.data;
  }
  return [];
}

function isFiniteTenantId(value) {
  if (value === undefined || value === null || value === '') return false;
  const n = Number(value);
  return Number.isFinite(n);
}

/**
 * Resolves tenant for create/update payloads: explicit value → JWT → session.
 * Matches backend {@code KnowledgeArticleRequest.tenantId} (Long).
 */
function resolveTenantIdForArticleBody(priorityValue) {
  if (isFiniteTenantId(priorityValue)) {
    return Number(priorityValue);
  }
  const user = getCurrentUser();
  if (user?.tenantId != null && user.tenantId !== '') {
    const n = Number(user.tenantId);
    if (Number.isFinite(n)) return n;
  }
  return getTenantIdFromSession();
}

function withTenantIdOnBody(articleData = {}) {
  const body = { ...articleData };
  const fromSnake = body.tenant_id;
  const candidate = body.tenantId ?? fromSnake;
  const tid = resolveTenantIdForArticleBody(candidate);
  if (tid !== undefined) {
    body.tenantId = tid;
  }
  if (Object.prototype.hasOwnProperty.call(body, 'tenant_id')) {
    delete body.tenant_id;
  }
  return body;
}

/**
 * Knowledge Article Service
 * Handles all API calls related to knowledge articles
 */
export const articleService = {
  /**
   * Get all knowledge articles (authenticated)
   * GET /gateway/registration/api/signup/knowledge-articles
   * 
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (optional)
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.search - Search term (optional)
   * @param {boolean} params.includeDrafts - Include draft articles (for admin only)
   * @param {number} params.tenantId - Filter by tenant ID (optional)
   * @returns {Promise} Response with articles array
   */
  getArticles: async (params = {}) => {
    try {
      const merged = { ...params };
      if (merged.tenantId === undefined) {
        const tid = resolveTenantIdForReadParams(merged);
        if (tid !== undefined) merged.tenantId = tid;
      }
      // Use authenticated endpoint through gateway
      const response = await apiRequest('/gateway/registration/api/signup/knowledge-articles', {
        method: 'GET',
        params: merged
      });
      
      // Registration service ResponseBean structure: { status, message, data }
      // Wrap response for consistent handling
      return { data: response };
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw error;
    }
  },

  /**
   * Get article by ID (authenticated)
   * GET /gateway/registration/api/signup/knowledge-articles/{articleId}?includeDrafts=
   * 
   * @param {number} articleId - Article ID
   * @param {Object} [options]
   * @param {boolean} [options.includeDrafts] - When true, drafts are visible (agent knowledge UI). Default: only active/published.
   * @returns {Promise} Response with article details
   */
  getArticleById: async (articleId, options = {}) => {
    try {
      const params = {};
      if (options.includeDrafts === true) {
        params.includeDrafts = true;
      }
      const response = await apiRequest(`/gateway/registration/api/signup/knowledge-articles/${articleId}`, {
        method: 'GET',
        params
      });
      
      if (response?.data) {
        return { data: response };
      }
      return { data: response };
    } catch (error) {
      console.error('Error fetching article:', error);
      throw error;
    }
  },

  /**
   * Create a new knowledge article (authenticated)
   * POST /gateway/registration/api/signup/knowledge-articles
   * 
   * @param {Object} articleData - Article data
   * @param {string} articleData.title - Article title
   * @param {string} articleData.question - Article question
   * @param {string} articleData.answer - Article answer
   * @param {string} articleData.category - Article category
   * @param {string[]} articleData.tags - Article tags
   * @param {string} articleData.ticketId - Associated ticket ID (optional)
   * @param {number} [articleData.tenantId] - Tenant scope (defaults from JWT / session when omitted)
   * @returns {Promise} Response with created article
   */
  createArticle: async (articleData) => {
    try {
      const body = withTenantIdOnBody(articleData);
      const response = await apiRequest('/gateway/registration/api/signup/knowledge-articles', {
        method: 'POST',
        body
      });
      
      // Registration service ResponseBean structure: { status, message, data }
      // No 'success' field - check status code or data presence
      // Wrap response for consistent handling
      return { data: response };
    } catch (error) {
      console.error('Error creating article:', error);
      throw error;
    }
  },

  /**
   * Update an existing knowledge article (authenticated)
   * PUT /gateway/registration/api/signup/knowledge-articles/{articleId}
   * 
   * @param {number} articleId - Article ID
   * @param {Object} articleData - Updated article data
   * @returns {Promise} Response with updated article
   */
  updateArticle: async (articleId, articleData) => {
    try {
      const body = withTenantIdOnBody(articleData);
      const response = await apiRequest(`/gateway/registration/api/signup/knowledge-articles/${articleId}`, {
        method: 'PUT',
        body
      });
      
      if (response?.data) {
        return { data: response };
      }
      return { data: response };
    } catch (error) {
      console.error('Error updating article:', error);
      throw error;
    }
  },

  /**
   * Search articles (authenticated)
   * GET /gateway/registration/api/signup/knowledge-articles/search?q={searchTerm}&tenantId=
   * 
   * @param {string} searchTerm - Search term
   * @param {Object} [options]
   * @param {number} [options.tenantId] - Defaults to sessionStorage tenantId when set; prefer JWT tenant for staff users
   * @param {boolean} [options.includeDrafts] - When true with tenantId, search includes drafts and all statuses (My Articles / admin)
   * @returns {Promise} Response with matching articles
   */
  searchArticles: async (searchTerm, options = {}) => {
    try {
      const params = { q: searchTerm };
      const tenantId = resolveTenantIdForReadParams(options);
      if (tenantId !== undefined) {
        params.tenantId = tenantId;
      }
      if (options.includeDrafts === true) {
        params.includeDrafts = true;
      }
      const response = await apiRequest('/gateway/registration/api/signup/knowledge-articles/search', {
        method: 'GET',
        params
      });
      
      if (response?.data) {
        return { data: response };
      }
      return { data: response };
    } catch (error) {
      console.error('Error searching articles:', error);
      throw error;
    }
  },

  /**
   * Search published/active articles without JWT (public landing, sign-in page).
   * GET /gateway/registration/api/no-auth/knowledge-articles/search?q=&tenantId=
   * Same server rules as authenticated customer search (active/published only); optional tenant scopes results.
   *
   * @param {string} searchTerm
   * @param {Object} [options]
   * @param {number} [options.tenantId] - When set, same as customer portal; otherwise all tenants (dev only).
   */
  searchArticlesNoAuth: async (searchTerm, options = {}) => {
    const q = (searchTerm || '').trim();
    if (!q) {
      throw new Error('Search term is required');
    }
    try {
      const params = { q };
      const tenantId = resolveTenantIdForReadParams(options);
      if (tenantId !== undefined) {
        params.tenantId = tenantId;
      }
      const response = await apiRequest('/gateway/registration/api/no-auth/knowledge-articles/search', {
        method: 'GET',
        params
      });
      if (response?.data) {
        return { data: response };
      }
      return { data: response };
    } catch (error) {
      console.error('Error searching articles (no-auth):', error);
      throw error;
    }
  },

  /**
   * Get articles by category
   * Uses the main getArticles endpoint with category filter
   * 
   * @param {string} category - Category name
   * @returns {Promise} Response with articles in category
   */
  getArticlesByCategory: async (category) => {
    return articleService.getArticles({ category });
  },

  /**
   * Increment used count for an article (authenticated)
   * POST /gateway/registration/api/signup/knowledge-articles/{articleId}/increment-used
   * 
   * @param {number} articleId - Article ID
   * @returns {Promise} Success response
   */
  incrementUsedCount: async (articleId) => {
    try {
      const response = await apiRequest(`/gateway/registration/api/signup/knowledge-articles/${articleId}/increment-used`, {
        method: 'POST'
      });
      
      return { data: response };
    } catch (error) {
      console.error('Error incrementing used count:', error);
      throw error;
    }
  },

  /**
   * Increment helpful count for an article (authenticated)
   * POST /gateway/registration/api/signup/knowledge-articles/{articleId}/increment-helpful
   * 
   * @param {number} articleId - Article ID
   * @returns {Promise} Success response
   */
  incrementHelpfulCount: async (articleId) => {
    try {
      const response = await apiRequest(`/gateway/registration/api/signup/knowledge-articles/${articleId}/increment-helpful`, {
        method: 'POST'
      });
      
      return { data: response };
    } catch (error) {
      console.error('Error incrementing helpful count:', error);
      throw error;
    }
  },

  /**
   * Increment not helpful count for an article (authenticated)
   * POST /gateway/registration/api/signup/knowledge-articles/{articleId}/increment-not-helpful
   * 
   * @param {number} articleId - Article ID
   * @returns {Promise} Success response
   */
  incrementNotHelpfulCount: async (articleId) => {
    try {
      const response = await apiRequest(`/gateway/registration/api/signup/knowledge-articles/${articleId}/increment-not-helpful`, {
        method: 'POST'
      });
      
      return { data: response };
    } catch (error) {
      console.error('Error incrementing not helpful count:', error);
      throw error;
    }
  },

  /**
   * Approve draft article (change status from draft to active)
   * POST /gateway/registration/api/signup/knowledge-articles/{articleId}/approve
   * 
   * @param {number} articleId - Article ID
   * @returns {Promise} Response with approved article
   */
  approveArticle: async (articleId) => {
    try {
      const response = await apiRequest(`/gateway/registration/api/signup/knowledge-articles/${articleId}/approve`, {
        method: 'POST'
      });
      
      return { data: response };
    } catch (error) {
      console.error('Error approving article:', error);
      throw error;
    }
  },

  /**
   * Get article attachments
   * GET /gateway/file-service/jwt/file/article/{articleId}/attachments
   * 
   * @param {number} articleId - Article ID
   * @returns {Promise} Response with attachments array
   */
  getArticleAttachments: async (articleId) => {
    try {
      const response = await apiRequest(`/gateway/file-service/jwt/file/article/${articleId}/attachments`, {
        method: 'GET'
      });
      // Backend ResponseUtil.successResponse returns: { status: "success", response: [...] }
      // apiRequest wraps it, so structure is: response = { status: "success", response: [...] }
      // Return as-is so ViewArticleModal can parse it correctly
      return response;
    } catch (error) {
      console.error('Error fetching article attachments:', error);
      // Return empty array on error instead of throwing
      return { response: [] };
    }
  },

  /**
   * Download article attachment
   * GET /gateway/file-service/jwt/file/article/{articleId}/attachment/{attachmentId}/download
   * 
   * @param {number} articleId - Article ID
   * @param {number} attachmentId - Attachment ID
   * @returns {Promise<Blob>} File blob
   */
  downloadArticleAttachment: async (articleId, attachmentId) => {
    try {
      // Get BASE_URL from apiClient logic
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const BASE_URL = hostname.includes('localhost') || hostname.includes('127.0.0.1')
        ? 'http://localhost:8080'
        : `${protocol}//${hostname}`;

      const token = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");

      const response = await fetch(`${BASE_URL}/gateway/file-service/jwt/file/article/${articleId}/attachment/${attachmentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/octet-stream'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download attachment: ${response.statusText}`);
      }

      // Return blob directly
      return await response.blob();
    } catch (error) {
      console.error('Error downloading article attachment:', error);
      throw error;
    }
  }
};

